"use client";

import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { portfolioKeys } from "../../hooks/queries";
import { putPortfolioPositionsTablePreference } from "../../services/user-ui-preferences-service";
import {
  PORTFOLIO_POSITIONS_ALWAYS_VISIBLE,
  PORTFOLIO_POSITIONS_STABLE_PREFIX,
  getPortfolioPositionsModalColumnConfig,
} from "../../utils/positions-table-preferences";

interface PositionsColumnsSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnOrder: string[];
  visibility: Record<string, boolean>;
}

function SortableRow({
  id,
  label,
  visible,
  canToggle,
  canReorder,
  onToggle,
}: {
  id: string;
  label: string;
  visible: boolean;
  canToggle: boolean;
  canReorder: boolean;
  onToggle: (next: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !canReorder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const alwaysVisible = PORTFOLIO_POSITIONS_ALWAYS_VISIBLE.has(id);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 rounded-lg border border-border/50 bg-card px-3 py-2.5",
        isDragging && "z-10 opacity-90 shadow-md",
      )}
    >
      <button
        type="button"
        className={cn(
          "touch-none text-muted-foreground hover:text-foreground p-1 -m-1 rounded shrink-0",
          !canReorder && "invisible pointer-events-none",
        )}
        aria-label={`Reorder ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex flex-1 items-center gap-3 min-w-0">
        {canToggle ? (
          <>
            <Checkbox
              id={`col-${id}`}
              checked={visible}
              onCheckedChange={(v) => onToggle(v === true)}
            />
            <Label
              htmlFor={`col-${id}`}
              className="text-sm font-medium cursor-pointer truncate"
            >
              {label}
            </Label>
          </>
        ) : (
          <span className="text-sm font-medium text-muted-foreground truncate">
            {label}
            {alwaysVisible && (
              <span className="ml-2 text-xs font-normal">(always on)</span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export function PositionsColumnsSettingsModal({
  open,
  onOpenChange,
  columnOrder,
  visibility,
}: PositionsColumnsSettingsModalProps) {
  const queryClient = useQueryClient();
  const columnMeta = useMemo(() => getPortfolioPositionsModalColumnConfig(), []);
  const metaById = useMemo(() => {
    const m = new Map(columnMeta.map((c) => [c.id, c]));
    return m;
  }, [columnMeta]);

  const [order, setOrder] = useState<string[]>(columnOrder);
  const [vis, setVis] = useState<Record<string, boolean>>({ ...visibility });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setOrder([...columnOrder]);
      setVis({ ...visibility });
      setError(null);
    }
  }, [open, columnOrder, visibility]);

  const sortableIds = useMemo(
    () =>
      order.filter(
        (id) =>
          !PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(
            id as (typeof PORTFOLIO_POSITIONS_STABLE_PREFIX)[number],
          ),
      ),
    [order],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const prefix = [...PORTFOLIO_POSITIONS_STABLE_PREFIX];
    const rest = order.filter(
      (id) =>
        !PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(
          id as (typeof PORTFOLIO_POSITIONS_STABLE_PREFIX)[number],
        ),
    );
    const oldIndex = rest.indexOf(String(active.id));
    const newIndex = rest.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const nextRest = arrayMove(rest, oldIndex, newIndex);
    setOrder([...prefix, ...nextRest]);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await putPortfolioPositionsTablePreference({
        column_order: order,
        visibility: vis,
      });
      await queryClient.invalidateQueries({
        queryKey: portfolioKeys.uiPrefs.portfolioPositionsTable(),
      });
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[85vh] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Positions columns</DialogTitle>
          <DialogDescription>
            Toggle optional columns and drag rows to reorder. Nothing is saved until you
            click Save.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 overflow-y-auto flex-1 min-h-0 space-y-4">
          <p className="text-xs text-muted-foreground">
            Asset and Trades stay on the left. Value, Net P&L and Net Return always stay
            visible.
          </p>

          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div className="flex flex-col gap-2">
                {order.map((id) => {
                  const meta = metaById.get(id);
                  if (!meta) return null;
                  const isStable = PORTFOLIO_POSITIONS_STABLE_PREFIX.includes(
                    id as (typeof PORTFOLIO_POSITIONS_STABLE_PREFIX)[number],
                  );
                  if (isStable) {
                    return (
                      <div
                        key={id}
                        className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5"
                      >
                        <div className="w-6 shrink-0" aria-hidden />
                        <span className="text-sm font-medium text-muted-foreground truncate">
                          {meta.label}
                          <span className="ml-2 text-xs font-normal">(fixed)</span>
                        </span>
                      </div>
                    );
                  }
                  return (
                    <SortableRow
                      key={id}
                      id={id}
                      label={meta.label}
                      visible={meta.canHide ? (vis[id] ?? false) : true}
                      canToggle={meta.canHide}
                      canReorder={meta.canReorder}
                      onToggle={(next) =>
                        setVis((prev) => ({
                          ...prev,
                          [id]: next,
                        }))
                      }
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border/40 bg-muted/20 gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
